import axios from 'axios';
import { buildUpsertRow } from '@/utils/receptionistKb';
import { buildDashboardStatBody, statErrorToState, normalizeRange } from '@/utils/dashboardStats';
import { getIdToken as getFirebaseIdToken } from '@/pages/MyAccount/components/firebaseIdToken';

// Set default base URL for all axios requests in this module
axios.defaults.baseURL = process.env.VUE_APP_TEAMIO_API_URL;

const namespaced = true;

/**
 * Axios config carrying the caller's Firebase ID token.
 *
 * teamio-bot's /admin/* and /receptionist-kb/* endpoints identify the caller from this token and
 * IGNORE any employeeId in the request (security audit S1, 2026-09-17: an employeeId alone used to
 * be enough, so a Super Admin's id opened every site). Without the header they 401.
 * getFirebaseIdToken waits out Firebase's async init, so a deep-link mount doesn't race it.
 */
async function botAuth(rootState, config = {}) {
  const idToken = await getFirebaseIdToken(rootState && rootState.getAuth ? rootState.getAuth() : null);
  const headers = { ...(config.headers || {}) };
  if (idToken) headers.Authorization = `Bearer ${idToken}`;
  return { ...config, headers };
}

const state = {
  stats: {
    textBot: {
      totalConversations: 0,
      todayConversations: 0,
      avgLength: '0m'
    },
    voiceBot: {
      totalCalls: 0,
      todayCalls: 0,
      avgDuration: '0m'
    },
    appointments: {
      totalBookings: 0,
      todayBookings: 0,
      conversionRate: '0%'
    }
  },

  // --- Teamio AI Dashboard — three REAL tiles (DB-backed; teamio-bot
  // lib/dashboardStats.js). Each tile tracks its own data + loading + accessState
  // so one tile failing/403ing never blanks the others. accessState:
  //   'ok'            -> show the real number (0 included, honestly)
  //   'not-available' -> 403 (site/admin gate) — friendly, NOT an error
  //   'error'         -> any other failure
  // Numbers default to 0 but are ONLY rendered once accessState === 'ok' AND the
  // tile is not loading; loading/not-available/error replace the number entirely.
  dashboard: {
    agent: { total: 0, today: 0, range: 0 },
    receptionist: { callsTotal: 0, callsToday: 0, callsRange: 0, textsTotal: 0, textsToday: 0, textsRange: 0 },
    appointments: { total: 0, today: 0, range: 0 },
  },
  // The single source of truth for the dashboard date-range picker. The picker
  // (AdminLayout), the Dashboard tiles, and the AppointmentStats page all read this
  // so they always agree on the selected window. Default 'all' = all-time totals.
  dashboardRange: 'all',
  dashboardLoading: {
    agent: false,
    receptionist: false,
    appointments: false,
  },
  // 'ok' | 'not-available' | 'error'
  dashboardAccess: {
    agent: 'ok',
    receptionist: 'ok',
    appointments: 'ok',
  },

  elevenLabsConversations: [],
  elevenLabsConversationDetail: null,
  elevenLabsConversationPagination: {
    nextCursor: null,
    hasMore: false,
    currentPageNumber: 1,
    currentFilters: {},
  },

  // --- Per-site AI call history (DB-backed; replaces the ElevenLabs proxy list) ---
  siteCalls: [],
  siteCallsNextCursor: null,
  siteCallsCurrentPageNumber: 1,
  siteCallsCurrentFilters: {},
  isLoadingSiteCalls: false,
  siteCallsError: null,

  // --- Receptionist Knowledge Base (review/approve UI) ---
  kbItems: [],
  isLoadingKb: false,
  kbError: null,
  // 'ok' (default) | 'not-enabled' (site not AI_KB_ENABLED / not site admin -> 403).
  // The Knowledge tab renders a friendly state for 'not-enabled' instead of an error.
  kbAccessState: 'ok',
  kbUnknownDefault: 'message',

  isLoadingElevenLabsConversations: false,
  isLoadingElevenLabsConversationDetail: false,
  elevenLabsConversationError: null,
  
  isLoading: false,
  error: null,

  // New state for individual conversation audio
  elevenLabsAudioSrc: null,
  elevenLabsLoadingAudio: false,
  elevenLabsAudioError: null,

  elevenLabsLoadingDetail: false,
  elevenLabsErrorDetail: null,
};

const mutations = {
  SET_LOADING(state, isLoading) {
    state.isLoading = isLoading;
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
  SET_STATS(state, { type, stats }) {
    if (type === 'text') {
      state.stats.textBot = { ...state.stats.textBot, ...stats };
    } else if (type === 'voice') {
      state.stats.voiceBot = { ...state.stats.voiceBot, ...stats };
    } else if (type === 'appointments') {
      state.stats.appointments = { ...state.stats.appointments, ...stats };
    }
  },

  // --- Teamio AI Dashboard tile mutations (tile = 'agent'|'receptionist'|'appointments') ---
  SET_DASHBOARD_LOADING(state, { tile, isLoading }) {
    state.dashboardLoading = { ...state.dashboardLoading, [tile]: isLoading };
  },
  SET_DASHBOARD_ACCESS(state, { tile, accessState }) {
    state.dashboardAccess = { ...state.dashboardAccess, [tile]: accessState || 'ok' };
  },
  SET_DASHBOARD_STATS(state, { tile, stats }) {
    state.dashboard = { ...state.dashboard, [tile]: { ...state.dashboard[tile], ...stats } };
  },
  // The dashboard date-range picker's selected window. Normalized to a supported
  // key (unknown/missing -> 'all') so the value is always one the backend understands.
  SET_DASHBOARD_RANGE(state, range) {
    state.dashboardRange = normalizeRange(range);
  },
  SET_ELEVENLABS_LOADING_CONVERSATIONS(state, isLoading) {
    state.isLoadingElevenLabsConversations = isLoading;
  },
  SET_ELEVENLABS_LOADING_CONVERSATION_DETAIL(state, isLoading) {
    state.isLoadingElevenLabsConversationDetail = isLoading;
  },
  SET_ELEVENLABS_CONVERSATION_ERROR(state, error) {
    state.elevenLabsConversationError = error;
  },
  SET_ELEVENLABS_CONVERSATIONS(state, { conversations, pagination, filters }) {
    state.elevenLabsConversations = conversations;
    state.elevenLabsConversationPagination = {
      nextCursor: pagination.next_cursor || null,
      hasMore: pagination.has_more || false,
      currentPageNumber: 1,
      currentFilters: filters,
    };
    state.elevenLabsConversationError = null;
  },
  ADD_ELEVENLABS_CONVERSATIONS(state, { conversations, pagination }) {
    state.elevenLabsConversations.push(...conversations);
    state.elevenLabsConversationPagination = {
      ...state.elevenLabsConversationPagination,
      nextCursor: pagination.next_cursor || null,
      hasMore: pagination.has_more || false,
      currentPageNumber: state.elevenLabsConversationPagination.currentPageNumber + 1,
    };
  },
  RESET_ELEVENLABS_CONVERSATIONS_FOR_NEW_FILTER(state) {
    state.elevenLabsConversations = [];
    state.elevenLabsConversationPagination = {
      nextCursor: null,
      hasMore: false,
      currentPageNumber: 1,
      currentFilters: {},
    };
    state.elevenLabsConversationError = null;
  },
  SET_ELEVENLABS_CONVERSATION_DETAIL(state, detail) {
    state.elevenLabsConversationDetail = detail;
    state.elevenLabsConversationError = null;
  },
  SET_ELEVENLABS_LOADING_DETAIL(state, isLoading) {
    state.elevenLabsLoadingDetail = isLoading;
  },
  SET_ELEVENLABS_ERROR_DETAIL(state, error) {
    state.elevenLabsErrorDetail = error;
  },

  // Mutations for audio
  SET_ELEVENLABS_AUDIO_SRC(state, src) {
    // If the old src was a blob URL, revoke it
    if (state.elevenLabsAudioSrc && state.elevenLabsAudioSrc.startsWith('blob:')) {
      URL.revokeObjectURL(state.elevenLabsAudioSrc);
    }
    state.elevenLabsAudioSrc = src;
    state.elevenLabsAudioError = null; // Clear error on new src
  },
  SET_ELEVENLABS_LOADING_AUDIO(state, isLoading) {
    state.elevenLabsLoadingAudio = isLoading;
  },
  SET_ELEVENLABS_AUDIO_ERROR(state, error) {
    state.elevenLabsAudioError = error;
  },
  CLEAR_ELEVENLABS_AUDIO_DETAIL(state) {
    if (state.elevenLabsAudioSrc && state.elevenLabsAudioSrc.startsWith('blob:')) {
      URL.revokeObjectURL(state.elevenLabsAudioSrc);
    }
    state.elevenLabsAudioSrc = null;
    state.elevenLabsAudioError = null;
    state.isLoadingElevenLabsAudio = false;
  },

  CLEAR_ELEVENLABS_CONVERSATION_DETAIL(state) {
    state.elevenLabsConversationDetail = null;
  },
  REMOVE_ELEVENLABS_CONVERSATION_FROM_LIST(state, conversationId) {
    state.elevenLabsConversations = state.elevenLabsConversations.filter(c => c.conversation_id !== conversationId);
  },

  // --- Per-site AI call history mutations ---
  SET_SITE_CALLS_LOADING(state, isLoading) {
    state.isLoadingSiteCalls = isLoading;
  },
  SET_SITE_CALLS_ERROR(state, error) {
    state.siteCallsError = error;
  },
  SET_SITE_CALLS(state, { calls, nextCursor, filters }) {
    state.siteCalls = calls;
    state.siteCallsNextCursor = nextCursor || null;
    state.siteCallsCurrentPageNumber = 1;
    state.siteCallsCurrentFilters = filters || {};
    state.siteCallsError = null;
  },
  ADD_SITE_CALLS(state, { calls, nextCursor }) {
    state.siteCalls.push(...calls);
    state.siteCallsNextCursor = nextCursor || null;
    state.siteCallsCurrentPageNumber = state.siteCallsCurrentPageNumber + 1;
  },
  RESET_SITE_CALLS(state) {
    state.siteCalls = [];
    state.siteCallsNextCursor = null;
    state.siteCallsCurrentPageNumber = 1;
    state.siteCallsCurrentFilters = {};
    state.siteCallsError = null;
  },

  // --- Receptionist Knowledge Base mutations ---
  SET_KB_LOADING(state, isLoading) {
    state.isLoadingKb = isLoading;
  },
  SET_KB_ERROR(state, error) {
    state.kbError = error;
  },
  SET_KB_ITEMS(state, items) {
    state.kbItems = Array.isArray(items) ? items : [];
  },
  SET_KB_ACCESS_STATE(state, accessState) {
    state.kbAccessState = accessState || 'ok';
  },
  SET_KB_UNKNOWN_DEFAULT(state, value) {
    state.kbUnknownDefault = value || 'message';
  },
};

/**
 * Build the POST body for /admin/site-call-history from the active filter set.
 *
 * Only forwards filter keys the backend understands and only when they carry a
 * meaningful value (an explicit 'all' / null / undefined is treated as "no
 * filter" and omitted) so the server applies its own defaults.
 *
 * Exported (and mirrored in botAdmin-call-history.test.js) so the body shape is
 * unit-testable without spinning up axios/Vuex.
 */
export function buildSiteCallHistoryBody({ siteId, employeeId, filters = {}, cursor = null, limit } = {}) {
  const cleanFilters = {};
  if (filters.needsAttention) cleanFilters.needsAttention = true;
  if (filters.sentiment && filters.sentiment !== 'all') cleanFilters.sentiment = filters.sentiment;
  if (filters.outcome && filters.outcome !== 'all') cleanFilters.outcome = filters.outcome;
  if (filters.mode && filters.mode !== 'all') cleanFilters.mode = filters.mode;
  if (filters.from) cleanFilters.from = filters.from;
  if (filters.to) cleanFilters.to = filters.to;

  // employeeId identifies the logged-in user for the backend's DB-backed
  // authorization (site Admin → own site; Super Admin → any site). The
  // endpoint returns 403 without it. Always include the key (even when null,
  // e.g. not-yet-hydrated user) so the request shape is stable and the
  // backend can return its own auth error rather than us silently dropping it.
  const body = { siteId, employeeId: employeeId || null, filters: cleanFilters };
  if (cursor) body.cursor = cursor;
  if (limit) body.limit = limit;
  return body;
}

/**
 * Build the POST body shared by every receptionist-kb/* endpoint.
 *
 * Like the call-history body, `employeeId` is ALWAYS included (even when null, e.g.
 * not-yet-hydrated user) so the request shape is stable and the backend returns its
 * own 403 rather than us silently dropping it. Extra args (status/category, row,
 * ids, id/enabled, unknownDefault) are merged in by the caller.
 *
 * Exported (and mirrored in receptionist-kb-curation.test.js) so the identity-in-body
 * contract is unit-testable without axios/Vuex.
 */
export function buildKbBody({ siteId, employeeId, ...rest } = {}) {
  return { siteId, employeeId: employeeId || null, ...rest };
}

/**
 * Shared fetch for a single Teamio AI Dashboard tile.
 *
 * Sets the tile loading -> POSTs { siteId, employeeId } to `path` -> on success
 * commits the picked stats and accessState 'ok' -> on failure maps 403 to
 * 'not-available' (friendly) and everything else to 'error', WITHOUT touching the
 * stored numbers (so we never replace a real number with a fabricated one). Returns
 * { success, ...data } on success (data = the FULL unwrapped response so callers can
 * read extras like trend/recent), or { success:false, notAvailable?, error? }.
 *
 * `employeeId` comes from the logged-in user (customer store) — same source/path as
 * fetchSiteCallHistory / the KB actions.
 */
async function fetchDashboardTile({ commit, rootState, siteId, tile, path, pick }) {
  commit('SET_DASHBOARD_LOADING', { tile, isLoading: true });

  if (!siteId) {
    commit('SET_DASHBOARD_LOADING', { tile, isLoading: false });
    commit('SET_DASHBOARD_ACCESS', { tile, accessState: 'error' });
    return { success: false, error: 'Site ID is required.' };
  }

  const employeeId = rootState.customer && rootState.customer.user ? rootState.customer.user.EmployeeID : null;
  // The selected date-range window (single source of truth in this module). The
  // endpoint owns the site-local windowing for the key; 'all' = all-time totals.
  const range = rootState.botAdmin ? rootState.botAdmin.dashboardRange : 'all';
  const body = buildDashboardStatBody({ siteId, employeeId, range });

  try {
    const response = await axios.post(path, body, await botAuth(rootState));
    const data = response.data || {};
    commit('SET_DASHBOARD_STATS', { tile, stats: pick(data) });
    commit('SET_DASHBOARD_ACCESS', { tile, accessState: 'ok' });
    return { success: true, ...data };
  } catch (error) {
    const accessState = statErrorToState(error); // 403 -> 'not-available', else 'error'
    commit('SET_DASHBOARD_ACCESS', { tile, accessState });
    if (accessState !== 'not-available') {
      console.error(`Error fetching dashboard ${tile} stats:`, error.response || error);
    }
    const errorMsg = error.response?.data?.message || error.message || 'Failed to load stats.';
    return { success: false, notAvailable: accessState === 'not-available', error: errorMsg };
  } finally {
    commit('SET_DASHBOARD_LOADING', { tile, isLoading: false });
  }
}

const actions = {
  // Fetch conversations with optional filters
  async fetchConversations({ commit }, { filters = {}, page = 1, limit = 20 } = {}) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    
    try {
      // In a real implementation, this would be an API call
      // For now, simulate a successful response with mock data
      
      // Mock successful response after a delay
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Mock conversation data
      const mockConversations = [
        {
          id: 'conv_123456789',
          type: 'voice',
          date: new Date(Date.now() - 15 * 60 * 1000), // 15 minutes ago
          formattedDate: '2023-10-15 14:30:45',
          user: 'John Doe (555-123-4567)',
          duration: '2:34',
          status: 'completed',
          audioUrl: null
        },
        {
          id: 'conv_987654321',
          type: 'text',
          date: new Date(Date.now() - 3 * 60 * 60 * 1000), // 3 hours ago
          formattedDate: '2023-10-15 12:05:22',
          user: 'Jane Smith',
          duration: '4:15',
          status: 'completed',
          audioUrl: null
        },
        {
          id: 'conv_456789123',
          type: 'voice',
          date: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000), // 1 day ago
          formattedDate: '2023-10-14 09:17:03',
          user: 'Michael Johnson (555-888-9999)',
          duration: '1:48',
          status: 'failed',
          audioUrl: null
        },
        {
          id: 'conv_789123456',
          type: 'text',
          date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000), // 2 days ago
          formattedDate: '2023-10-13 16:22:41',
          user: 'Sarah Williams',
          duration: '5:30',
          status: 'in-progress',
          audioUrl: null
        }
      ];
      
      commit('SET_ELEVENLABS_CONVERSATIONS', { conversations: mockConversations, pagination: { page, limit, total: mockConversations.length, pages: Math.ceil(mockConversations.length / limit) }, filters: {} });
      
      // Return success response
      return {
        success: true,
        data: mockConversations,
        pagination: {
          page,
          limit,
          total: mockConversations.length,
          pages: Math.ceil(mockConversations.length / limit)
        }
      };
    } catch (error) {
      commit('SET_ERROR', 'Failed to load conversation data');
      return {
        success: false,
        error: 'Failed to load conversation data'
      };
    } finally {
      commit('SET_LOADING', false);
    }
  },
  
  // Fetch a single conversation by ID
  async fetchConversationById({ commit, dispatch }, conversationId) {
    if (!conversationId) {
      commit('SET_ERROR', 'Conversation ID is required');
      return { success: false, error: 'Conversation ID is required' };
    }
    
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    
    try {
      // In a real implementation, this would be an API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Mock conversation data
      const mockConversation = {
        id: conversationId,
        type: conversationId.includes('voice') ? 'voice' : 'text',
        formattedDate: '2023-10-15 14:30:00',
        user: 'John Doe (555-123-4567)',
        duration: '3:45',
        status: 'completed',
        audioUrl: null,
        relatedInfo: {
          appointment: {
            info: 'Teeth Cleaning with Dr. Smith',
            time: 'Tuesday, October 24th at 2:30 PM'
          },
          notes: 'Patient mentioned sensitivity in upper right molar. Flag for dentist to check during appointment.'
        }
      };
      
      commit('SET_ELEVENLABS_CONVERSATION_DETAIL', mockConversation);
      
      // If it's a voice conversation, also fetch the transcript and audio URL
      if (mockConversation.type === 'voice') {
        await dispatch('fetchConversationAudio', conversationId);
      }
      
      await dispatch('fetchConversationTranscript', conversationId);
      
      return {
        success: true,
        data: mockConversation
      };
    } catch (error) {
      commit('SET_ERROR', `Failed to load conversation details: ${error.message}`);
      return {
        success: false,
        error: `Failed to load conversation details: ${error.message}`
      };
    } finally {
      commit('SET_LOADING', false);
    }
  },
  
  // Fetch conversation transcript
  async fetchConversationTranscript({ commit, state }, conversationId) {
    if (!conversationId) {
      commit('SET_ERROR', 'Conversation ID is required');
      return { success: false, error: 'Conversation ID is required' };
    }
    
    commit('SET_LOADING', true);
    
    try {
      // In a real implementation, this would be an API call
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Mock transcript data
      const mockTranscript = [
        {
          role: 'bot',
          text: 'Hello! How can I help you today?',
          time: '14:30:02'
        },
        {
          role: 'user',
          text: 'I need to book a teeth cleaning appointment.',
          time: '14:30:45'
        },
        {
          role: 'bot',
          text: 'I can help you with that. When would you like to schedule your appointment?',
          time: '14:31:00'
        },
        {
          role: 'user',
          text: 'Do you have anything available next Tuesday?',
          time: '14:31:22'
        },
        {
          role: 'bot',
          text: 'Let me check our availability for next Tuesday, October 24th. We have slots at 10:00 AM, 2:30 PM, and 4:15 PM. Would any of these work for you?',
          time: '14:31:40'
        },
        {
          role: 'user',
          text: 'The 2:30 PM slot would be perfect.',
          time: '14:32:05'
        },
        {
          role: 'bot',
          text: 'Great! I\'ll book that for you. Can I get your name and phone number to confirm the appointment?',
          time: '14:32:20'
        },
        {
          role: 'user',
          text: 'My name is John Doe and my phone number is 555-123-4567.',
          time: '14:32:50'
        },
        {
          role: 'bot',
          text: 'Thank you, John. Your appointment has been booked for Tuesday, October 24th at 2:30 PM. You\'ll receive a confirmation text message shortly. Is there anything else you need help with?',
          time: '14:33:15'
        },
        {
          role: 'user',
          text: 'No, that\'s all. Thank you!',
          time: '14:33:35'
        },
        {
          role: 'bot',
          text: 'You\'re welcome! Have a great day, and we look forward to seeing you next Tuesday.',
          time: '14:33:45'
        }
      ];
      
      const base = state.elevenLabsConversationDetail || {};
      commit('SET_ELEVENLABS_CONVERSATION_DETAIL', { ...base, transcript: mockTranscript });
      
      return {
        success: true,
        data: mockTranscript
      };
    } catch (error) {
      commit('SET_ERROR', `Failed to load conversation transcript: ${error.message}`);
      return {
        success: false,
        error: `Failed to load conversation transcript: ${error.message}`
      };
    } finally {
      commit('SET_LOADING', false);
    }
  },
  
  // Fetch conversation audio URL
  async fetchConversationAudio({ commit }, conversationId) {
    if (!conversationId) {
      commit('SET_ERROR', 'Conversation ID is required');
      return { success: false, error: 'Conversation ID is required' };
    }
    
    try {
      // In a real implementation, this would call the ElevenLabs API
      // Use the GET /conversations/{conversation_id}/audio endpoint
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // 70% chance of success
      const success = Math.random() > 0.3;
      
      if (success) {
        // Mock audio URL
        const audioUrl = 'https://example.com/audio/conversation-123.mp3';
        commit('SET_ELEVENLABS_CONVERSATION_DETAIL', { ...state.elevenLabsConversationDetail, audioUrl });
        
        return {
          success: true,
          data: { audioUrl }
        };
      } else {
        throw new Error('Audio file not found or no longer available');
      }
    } catch (error) {
      return {
        success: false,
        error: `Failed to load audio recording: ${error.message}`
      };
    }
  },
  
  // Delete a conversation
  async deleteConversation({ commit }, conversationId) {
    if (!conversationId) {
      commit('SET_ERROR', 'Conversation ID is required');
      return { success: false, error: 'Conversation ID is required' };
    }
    
    commit('SET_LOADING', true);
    
    try {
      // In a real implementation, this would call the ElevenLabs API
      // Use the DELETE /conversations/{conversation_id} endpoint
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Remove from store
      commit('REMOVE_ELEVENLABS_CONVERSATION_FROM_LIST', conversationId);
      
      return {
        success: true,
        message: 'Conversation deleted successfully'
      };
    } catch (error) {
      commit('SET_ERROR', `Failed to delete conversation: ${error.message}`);
      return {
        success: false,
        error: `Failed to delete conversation: ${error.message}`
      };
    } finally {
      commit('SET_LOADING', false);
    }
  },
  
  // Fetch dashboard stats
  async fetchDashboardStats({ commit }) {
    commit('SET_LOADING', true);
    
    try {
      // In a real implementation, this would call the appropriate APIs
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1200));
      
      // Mock stats data
      const textBotStats = {
        totalConversations: 124,
        todayConversations: 8,
        avgLength: '4m 30s'
      };
      
      const voiceBotStats = {
        totalCalls: 67,
        todayCalls: 3,
        avgDuration: '2m 15s'
      };
      
      const appointmentStats = {
        totalBookings: 45,
        todayBookings: 2,
        conversionRate: '23%'
      };
      
      // Update store
      commit('SET_STATS', { type: 'text', stats: textBotStats });
      commit('SET_STATS', { type: 'voice', stats: voiceBotStats });
      commit('SET_STATS', { type: 'appointments', stats: appointmentStats });
      
      return {
        success: true,
        data: {
          textBot: textBotStats,
          voiceBot: voiceBotStats,
          appointments: appointmentStats
        }
      };
    } catch (error) {
      commit('SET_ERROR', 'Failed to load dashboard statistics');
      return {
        success: false,
        error: 'Failed to load dashboard statistics'
      };
    } finally {
      commit('SET_LOADING', false);
    }
  },

  // ===========================================================================
  // Teamio AI Dashboard — three REAL stat tiles.
  //
  // Same identity/auth pattern as fetchSiteCallHistory: POST { siteId, employeeId,
  // range } (employeeId from the logged-in user in the customer store; range from
  // this module's dashboardRange) to the bot. The endpoint owns the site-local
  // windowing for the range key, so we send NO from/to. A 403 (site/admin gate) sets
  // the tile's accessState to 'not-available' (a friendly "Not available" state, NOT
  // a red error); any other failure -> 'error'. Each tile's data/loading/access is
  // isolated so one failing tile never blanks the others, and we NEVER fabricate a
  // number on failure.
  //
  // Response shapes (teamio-bot lib/dashboardStats.js, unwrapped — response.data
  // IS the stats object; `range`/`*Range` are the range-scoped counts, == the
  // all-time total when range='all'):
  //   /admin/agent-stats         -> { total, today, range }
  //   /admin/receptionist-stats  -> { callsTotal, callsToday, callsRange, textsTotal, textsToday, textsRange }
  //   /admin/appointments-stats  -> { total, today, range, trend, recent }
  // ===========================================================================
  async fetchAgentStats({ commit, rootState }, { siteId } = {}) {
    return fetchDashboardTile({
      commit, rootState, siteId, tile: 'agent', path: '/admin/agent-stats',
      pick: (d) => ({ total: Number(d.total || 0), today: Number(d.today || 0), range: Number(d.range || 0) }),
    });
  },

  async fetchReceptionistStats({ commit, rootState }, { siteId } = {}) {
    return fetchDashboardTile({
      commit, rootState, siteId, tile: 'receptionist', path: '/admin/receptionist-stats',
      pick: (d) => ({
        callsTotal: Number(d.callsTotal || 0),
        callsToday: Number(d.callsToday || 0),
        callsRange: Number(d.callsRange || 0),
        textsTotal: Number(d.textsTotal || 0),
        textsToday: Number(d.textsToday || 0),
        textsRange: Number(d.textsRange || 0),
      }),
    });
  },

  async fetchAppointmentsStats({ commit, rootState }, { siteId } = {}) {
    return fetchDashboardTile({
      commit, rootState, siteId, tile: 'appointments', path: '/admin/appointments-stats',
      // The tile uses total/today/range; trend/recent belong to the Appointment
      // Stats page, so they're returned to the caller but not stored here.
      pick: (d) => ({ total: Number(d.total || 0), today: Number(d.today || 0), range: Number(d.range || 0) }),
    });
  },

  // Set the shared date-range window (commits the picker selection). The Dashboard /
  // AppointmentStats components watch the dashboardRange getter and re-fetch when it
  // changes, so this is the single entry point the picker calls on change.
  setDashboardRange({ commit }, range) {
    commit('SET_DASHBOARD_RANGE', range);
  },

  async fetchElevenLabsConversations({ commit, state, rootState }, { agentID, filters = {}, newSearchOrFilter = false }) {
    commit('SET_ELEVENLABS_LOADING_CONVERSATIONS', true);
    // Error state will be handled by success/failure commits
    
    if (!agentID) {
      commit('SET_ELEVENLABS_CONVERSATION_ERROR', 'Agent ID is required to fetch conversations.');
      commit('SET_ELEVENLABS_LOADING_CONVERSATIONS', false);
      return { success: false, error: 'Agent ID is required.' };
    }

    if (newSearchOrFilter) {
      commit('RESET_ELEVENLABS_CONVERSATIONS_FOR_NEW_FILTER');
    }

    const params = {
      agentID: agentID,
      // Use the cursor from the passed filters if available (for 'next page' calls for the current filter set)
      // If newSearchOrFilter is true, filters.cursor should effectively be null/undefined anyway as we reset.
      ...(filters.cursor && !newSearchOrFilter && { cursor: filters.cursor }), 
      ...(filters.call_successful !== undefined && { call_successful: filters.call_successful }),
      ...(filters.call_start_before_unix && { call_start_before_unix: filters.call_start_before_unix }),
      ...(filters.call_start_after_unix && { call_start_after_unix: filters.call_start_after_unix }),
      ...(filters.page_size && { page_size: filters.page_size }),
    };

    // Determine if this call is for a subsequent page (using a cursor)
    const isFetchingNextPage = params.cursor && !newSearchOrFilter;

    try {
      const url = '/admin/elevenlabs-conversations'; // USE RELATIVE URL
      
      console.log(`Fetching ElevenLabs conversations. IsNextPage: ${isFetchingNextPage}. Params:`, params);
      const response = await axios.get(url, await botAuth(rootState, { params })); // USE RELATIVE URL

      // Assign directly from response.data, assuming backend doesn't double-wrap
      const responseData = response.data; 
      console.log('Response Data:', JSON.stringify(responseData, null, 2));
      
      // --- Safely access pagination info (using traditional checks for ESLint compatibility) ---
      const paginationInfo = {
        next_cursor: responseData && responseData.next_cursor ? responseData.next_cursor : null,
        has_more: responseData && responseData.has_more ? responseData.has_more : false
      };

      // Determine conversations from the (now correctly assigned) responseData
      const conversationsToCommit = responseData && responseData.conversations ? responseData.conversations : [];

      if (isFetchingNextPage) { 
        commit('ADD_ELEVENLABS_CONVERSATIONS', { 
          conversations: conversationsToCommit, 
          pagination: paginationInfo 
        });
      } else { // Initial fetch or new filter criteria
        commit('SET_ELEVENLABS_CONVERSATIONS', { 
          conversations: conversationsToCommit, 
          pagination: paginationInfo,
          filters: filters // Store the filters that generated this list (excluding cursor itself)
        });
      }
      return { success: true, data: responseData };
    } catch (error) {
      console.error('Error fetching ElevenLabs conversations:', error.response || error);
      const errorMsg = error.response?.data?.message || error.message || 'Failed to load conversations.';
      commit('SET_ELEVENLABS_CONVERSATION_ERROR', errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      commit('SET_ELEVENLABS_LOADING_CONVERSATIONS', false);
    }
  },

  // Fetch per-site AI call history from the DB-backed endpoint.
  // Replaces the account-level ElevenLabs proxy as the list source.
  async fetchSiteCallHistory({ commit, rootState }, { siteId, filters = {}, cursor = null, limit, newSearchOrFilter = false } = {}) {
    commit('SET_SITE_CALLS_LOADING', true);

    if (!siteId) {
      commit('SET_SITE_CALLS_ERROR', 'Site ID is required to fetch call history.');
      commit('SET_SITE_CALLS_LOADING', false);
      return { success: false, error: 'Site ID is required.' };
    }

    if (newSearchOrFilter) {
      commit('RESET_SITE_CALLS');
    }

    // Logged-in user identity for the backend's DB-backed authorization.
    // Same `user` object the mixin exposes as `this.user` (customer store).
    const employeeId = rootState.customer && rootState.customer.user ? rootState.customer.user.EmployeeID : null;

    const isFetchingNextPage = !!cursor && !newSearchOrFilter;
    const body = buildSiteCallHistoryBody({ siteId, employeeId, filters, cursor: isFetchingNextPage ? cursor : null, limit });

    try {
      const response = await axios.post('/admin/site-call-history', body, await botAuth(rootState));
      const data = response.data || {};
      const calls = Array.isArray(data.calls) ? data.calls : [];
      const nextCursor = data.nextCursor || null;

      if (isFetchingNextPage) {
        commit('ADD_SITE_CALLS', { calls, nextCursor });
      } else {
        commit('SET_SITE_CALLS', { calls, nextCursor, filters });
      }
      return { success: true, data };
    } catch (error) {
      console.error('Error fetching site call history:', error.response || error);
      const errorMsg = error.response?.data?.message || error.message || 'Failed to load call history.';
      commit('SET_SITE_CALLS_ERROR', errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      commit('SET_SITE_CALLS_LOADING', false);
    }
  },

  async fetchElevenLabsConversationDetail({ commit, rootState }, payload) {
    // Accept either a bare conversationId (legacy) or { conversationId, siteID }.
    // siteID is now REQUIRED by the guarded backend endpoint (403 without it).
    const conversationId = typeof payload === 'object' && payload !== null ? payload.conversationId : payload;
    const siteID = typeof payload === 'object' && payload !== null ? payload.siteID : undefined;

    commit('SET_ELEVENLABS_LOADING_DETAIL', true);
    commit('SET_ELEVENLABS_ERROR_DETAIL', null);
    commit('CLEAR_ELEVENLABS_CONVERSATION_DETAIL'); // Clear previous detail

    if (!conversationId) {
      commit('SET_ELEVENLABS_CONVERSATION_ERROR', 'Conversation ID is required.');
      commit('SET_ELEVENLABS_LOADING_DETAIL', false);
      return { success: false, error: 'Conversation ID is required.' };
    }

    // Logged-in user identity for the backend's DB-backed authorization
    // (same source/path as fetchSiteCallHistory; endpoint 403s without it).
    const employeeId = rootState.customer && rootState.customer.user ? rootState.customer.user.EmployeeID : null;

    try {
      const url = `/admin/elevenlabs-conversations/${conversationId}`; // USE RELATIVE URL
      console.log(`Fetching Detail from URL: ${url}`); // ADD LOGGING
      // pass siteId + employeeId for the ownership/auth guard
      const params = {};
      if (siteID) params.siteId = siteID;
      if (employeeId) params.employeeId = employeeId;
      const response = await axios.get(url, await botAuth(rootState, { params }));
      //console.log('Raw Detail API Response:', JSON.stringify(response.data, null, 2)); // ADD LOGGING
      
      // Assume detail endpoint also returns data directly in response.data
      commit('SET_ELEVENLABS_CONVERSATION_DETAIL', response.data); 
      return { success: true, data: response.data };
    } catch (error) {
      console.error(`Error fetching ElevenLabs conversation detail ${conversationId}:`, error.response || error);
      const errorMsg = error.response?.data?.message || error.message || 'Failed to load conversation detail.';
      commit('SET_ELEVENLABS_CONVERSATION_ERROR', errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      commit('SET_ELEVENLABS_LOADING_DETAIL', false);
    }
  },

  async fetchElevenLabsAudio({ commit, rootState }, { conversationId, siteID }) {
    console.log(`[Vuex fetchElevenLabsAudio] Action called for convID: ${conversationId}, siteID: ${siteID}`);
    if (!conversationId) {
      commit('SET_ELEVENLABS_AUDIO_ERROR', 'Conversation ID is required to fetch audio.');
      console.error('[Vuex fetchElevenLabsAudio] Error: Conversation ID is required.');
      return;
    }

    commit('SET_ELEVENLABS_LOADING_AUDIO', true);
    commit('CLEAR_ELEVENLABS_AUDIO_DETAIL'); // Clear previous audio src and error

    try {
      const headers = {
        'Accept': 'audio/mpeg',
      };

      
      // siteID might be used by the backend proxy for routing or context, so it's logged here.
      // It's not directly added to headers unless the backend specifically requires it as a header.
      if (siteID) {
        console.log('[Vuex fetchElevenLabsAudio] siteID provided for context (if backend uses it):', siteID);
      }

      const apiUrl = process.env.VUE_APP_TEAMIO_API_URL || ''; // Ensure base URL is available
      const fullUrl = `${apiUrl}/admin/elevenlabs-conversations/${conversationId}/audio`;
      console.log('[Vuex fetchElevenLabsAudio] Requesting audio from URL:', fullUrl);

      // Logged-in user identity for the backend's DB-backed authorization
      // (same source/path as fetchSiteCallHistory; endpoint 403s without it).
      const employeeId = rootState.customer && rootState.customer.user ? rootState.customer.user.EmployeeID : null;
      const params = {};
      if (siteID) params.siteId = siteID; // required by the backend ownership guard
      if (employeeId) params.employeeId = employeeId;

      const response = await axios.get(fullUrl, {
        ...(await botAuth(rootState, { headers })),
        params,
        responseType: 'arraybuffer' // Important for binary data like audio
      });

      console.log('[Vuex fetchElevenLabsAudio] Audio data received, length:', response.data.byteLength);

      // Create a Blob from the arraybuffer
      const audioBlob = new Blob([response.data], { type: 'audio/mpeg' });
      // Create an object URL from the Blob
      const audioUrl = URL.createObjectURL(audioBlob);

      console.log('[Vuex fetchElevenLabsAudio] Created Blob URL:', audioUrl);
      commit('SET_ELEVENLABS_AUDIO_SRC', audioUrl);
      commit('SET_ELEVENLABS_LOADING_AUDIO', false);

    } catch (error) {
      let errorMessage = 'Failed to fetch audio.';
      if (error.response) {
        console.error('[Vuex fetchElevenLabsAudio] Error response:', error.response.status, error.response.data);
        // Try to get a more specific error message if the backend provides one (e.g., in a JSON error response)
        // For arraybuffer, error.response.data might not be easily readable text. Decode if necessary or rely on status.
        if (error.response.status === 401) {
          errorMessage = 'Unauthorized to fetch audio. Please check login.';
        } else if (error.response.status === 403) {
          errorMessage = 'Forbidden. You may not have permission to access this audio.';
        } else if (error.response.status === 404) {
          errorMessage = 'Audio recording not found.';
        } else {
          errorMessage = `Error ${error.response.status}: Could not load audio.`;
        }
      } else if (error.request) {
        console.error('[Vuex fetchElevenLabsAudio] Error request:', error.request);
        errorMessage = 'No response from server while fetching audio.';
      } else {
        console.error('[Vuex fetchElevenLabsAudio] Error message:', error.message);
        errorMessage = `Error fetching audio: ${error.message}`;
      }
      commit('SET_ELEVENLABS_AUDIO_ERROR', errorMessage);
      commit('SET_ELEVENLABS_LOADING_AUDIO', false);
      console.error('[Vuex fetchElevenLabsAudio] Catch block error complete.');
    }
  },

  async deleteElevenLabsConversation({ commit, dispatch }, conversationId) {
    // Optionally set a specific loading state for delete if needed
    commit('SET_ELEVENLABS_CONVERSATION_ERROR', null);

    if (!conversationId) {
      commit('SET_ELEVENLABS_CONVERSATION_ERROR', 'Conversation ID is required for deletion.');
      return { success: false, error: 'Conversation ID is required for deletion.' };
    }

    try {
      const url = `/admin/elevenlabs-conversations/${conversationId}`; // USE RELATIVE URL
      await axios.delete(url); // USE RELATIVE URL
      commit('REMOVE_ELEVENLABS_CONVERSATION_FROM_LIST', conversationId);
      commit('CLEAR_ELEVENLABS_CONVERSATION_DETAIL'); // Clear detail if the deleted one was active
      return { success: true };
    } catch (error) {
      console.error(`Error deleting ElevenLabs conversation ${conversationId}:`, error.response || error);
      const errorMsg = error.response?.data?.message || error.message || 'Failed to delete conversation.';
      commit('SET_ELEVENLABS_CONVERSATION_ERROR', errorMsg);
      return { success: false, error: errorMsg };
    }
  },

  // =========================================================================
  // Receptionist Knowledge Base actions.
  //
  // Same identity/auth pattern as the call-history actions: POST to the bot with
  // a body carrying { siteId, employeeId, ...args }. employeeId comes from the
  // logged-in user (customer store), the same source the call-history actions use.
  // The endpoints live at the ROOT path (receptionist-kb/*), NOT under /admin/.
  // =========================================================================

  // employeeId for the bot's DB-backed authz (same source/path as the call-history
  // actions: rootState.customer.user.EmployeeID).
  // (helper kept inline below per-action to avoid a shared closure dependency)

  async fetchKb({ commit, rootState }, { siteId, status, category } = {}) {
    commit('SET_KB_LOADING', true);
    commit('SET_KB_ERROR', null);

    if (!siteId) {
      commit('SET_KB_ERROR', 'Site ID is required to load the knowledge base.');
      commit('SET_KB_LOADING', false);
      return { success: false, error: 'Site ID is required.' };
    }

    const employeeId = rootState.customer && rootState.customer.user ? rootState.customer.user.EmployeeID : null;
    const body = buildKbBody({ siteId, employeeId });
    if (status) body.status = status;
    if (category) body.category = category;

    try {
      const response = await axios.post('/receptionist-kb/list', body, await botAuth(rootState));
      const data = response.data || {};
      const items = Array.isArray(data.items) ? data.items : [];
      commit('SET_KB_ITEMS', items);
      commit('SET_KB_ACCESS_STATE', 'ok');
      return { success: true, items };
    } catch (error) {
      const httpStatus = error.response ? error.response.status : null;
      // 403 == site not AI_KB_ENABLED OR caller not a site admin -> friendly
      // "not enabled" state, NOT a red error. Any other status is a real error.
      if (httpStatus === 403) {
        commit('SET_KB_ITEMS', []);
        commit('SET_KB_ACCESS_STATE', 'not-enabled');
        return { success: false, notEnabled: true };
      }
      console.error('Error fetching receptionist KB:', error.response || error);
      const errorMsg = error.response?.data?.message || error.message || 'Failed to load knowledge base.';
      commit('SET_KB_ERROR', errorMsg);
      commit('SET_KB_ACCESS_STATE', 'error');
      return { success: false, error: errorMsg };
    } finally {
      commit('SET_KB_LOADING', false);
    }
  },

  // Create or edit a row. `row` is the curate-form payload; buildUpsertRow strips
  // the server-controlled status/enabled keys before they can reach the backend.
  async upsertKb({ rootState }, { siteId, row } = {}) {
    if (!siteId) return { success: false, error: 'Site ID is required.' };
    const employeeId = rootState.customer && rootState.customer.user ? rootState.customer.user.EmployeeID : null;
    const body = buildKbBody({ siteId, employeeId, row: buildUpsertRow(row || {}) });

    try {
      const response = await axios.post('/receptionist-kb/upsert', body, await botAuth(rootState));
      return { success: true, id: (response.data || {}).id };
    } catch (error) {
      console.error('Error upserting receptionist KB row:', error.response || error);
      const errorMsg = error.response?.data?.message || error.message || 'Failed to save.';
      return { success: false, error: errorMsg };
    }
  },

  // Approve one or many rows (single == ids:[id]).
  async approveKb({ rootState }, { siteId, ids } = {}) {
    if (!siteId) return { success: false, error: 'Site ID is required.' };
    if (!Array.isArray(ids) || ids.length === 0) return { success: false, error: 'No rows to approve.' };
    const employeeId = rootState.customer && rootState.customer.user ? rootState.customer.user.EmployeeID : null;
    const body = buildKbBody({ siteId, employeeId, ids });

    try {
      const response = await axios.post('/receptionist-kb/approve', body, await botAuth(rootState));
      return { success: true, approved: (response.data || {}).approved };
    } catch (error) {
      console.error('Error approving receptionist KB rows:', error.response || error);
      const errorMsg = error.response?.data?.message || error.message || 'Failed to approve.';
      return { success: false, error: errorMsg };
    }
  },

  async setKbEnabled({ rootState }, { siteId, id, enabled } = {}) {
    if (!siteId) return { success: false, error: 'Site ID is required.' };
    const employeeId = rootState.customer && rootState.customer.user ? rootState.customer.user.EmployeeID : null;
    const body = buildKbBody({ siteId, employeeId, id, enabled: !!enabled });

    try {
      const response = await axios.post('/receptionist-kb/set-enabled', body, await botAuth(rootState));
      return { success: true, affected: (response.data || {}).affected };
    } catch (error) {
      console.error('Error toggling receptionist KB row:', error.response || error);
      const errorMsg = error.response?.data?.message || error.message || 'Failed to update.';
      return { success: false, error: errorMsg };
    }
  },

  async deleteKb({ rootState }, { siteId, id } = {}) {
    if (!siteId) return { success: false, error: 'Site ID is required.' };
    const employeeId = rootState.customer && rootState.customer.user ? rootState.customer.user.EmployeeID : null;
    const body = buildKbBody({ siteId, employeeId, id });

    try {
      const response = await axios.post('/receptionist-kb/delete', body, await botAuth(rootState));
      return { success: true, affected: (response.data || {}).affected };
    } catch (error) {
      console.error('Error deleting receptionist KB row:', error.response || error);
      const errorMsg = error.response?.data?.message || error.message || 'Failed to delete.';
      return { success: false, error: errorMsg };
    }
  },

  async fetchKbSettings({ commit, rootState }, { siteId } = {}) {
    if (!siteId) return { success: false, error: 'Site ID is required.' };
    const employeeId = rootState.customer && rootState.customer.user ? rootState.customer.user.EmployeeID : null;
    const body = buildKbBody({ siteId, employeeId });

    try {
      const response = await axios.post('/receptionist-kb/settings', body, await botAuth(rootState));
      const value = (response.data || {}).unknownDefault || 'message';
      commit('SET_KB_UNKNOWN_DEFAULT', value);
      return { success: true, unknownDefault: value };
    } catch (error) {
      // Settings live behind the same 403 gate; stay quiet (the list call already
      // surfaces the not-enabled state) and keep the default.
      console.error('Error fetching receptionist KB settings:', error.response || error);
      return { success: false, error: error.response?.data?.message || error.message };
    }
  },

  async setKbUnknownDefault({ commit, rootState }, { siteId, value } = {}) {
    if (!siteId) return { success: false, error: 'Site ID is required.' };
    const employeeId = rootState.customer && rootState.customer.user ? rootState.customer.user.EmployeeID : null;
    // Backend body key is `unknownDefault` (kbSetUnknownDefault).
    const body = buildKbBody({ siteId, employeeId, unknownDefault: value });

    try {
      const response = await axios.post('/receptionist-kb/set-unknown-default', body, await botAuth(rootState));
      const saved = (response.data || {}).unknownDefault || value;
      commit('SET_KB_UNKNOWN_DEFAULT', saved);
      return { success: true, unknownDefault: saved };
    } catch (error) {
      console.error('Error saving receptionist KB unknown-default:', error.response || error);
      const errorMsg = error.response?.data?.message || error.message || 'Failed to save.';
      return { success: false, error: errorMsg };
    }
  },

  // -------------------------------------------------------------------------
  // E3 — seed KB candidates from a document or the practice website.
  //
  // The file itself is uploaded to S3 by the component (uploadToS3, teamioType
  // 'RECEPTIONIST_KB'); this action only hands the bot the resulting s3Key +
  // filename. The bot (kbIngestDoc / kbIngestScrape) extracts FAQ candidates as
  // `needs_review` rows and returns a count summary — the component then refreshes
  // the list so the new candidates appear. Same identity/auth/403 pattern as the
  // other KB actions. The bot's friendly 400 message (unsupported file / bad URL /
  // unreadable doc) is surfaced verbatim so the panel can show it inline.
  // -------------------------------------------------------------------------

  // POST receptionist-kb/ingest-doc — body { siteId, employeeId, s3Key, filename }.
  // Returns { success, candidates, chunks, filename } on success.
  async ingestKbDoc({ rootState }, { siteId, s3Key, filename } = {}) {
    if (!siteId) return { success: false, error: 'Site ID is required.' };
    if (!s3Key || !filename) return { success: false, error: 'A file is required.' };
    const employeeId = rootState.customer && rootState.customer.user ? rootState.customer.user.EmployeeID : null;
    const body = buildKbBody({ siteId, employeeId, s3Key, filename });

    try {
      const response = await axios.post('/receptionist-kb/ingest-doc', body, await botAuth(rootState));
      const data = response.data || {};
      return {
        success: true,
        candidates: data.candidates || 0,
        chunks: data.chunks || 0,
        filename: data.filename || filename,
      };
    } catch (error) {
      console.error('Error ingesting receptionist KB document:', error.response || error);
      const httpStatus = error.response ? error.response.status : null;
      const errorMsg = error.response?.data?.message || error.message || 'Could not read that document.';
      // 403 == site no longer entitled / caller not an admin. Flag it so the
      // component re-lists and the friendly not-enabled state takes over (rather
      // than showing a bare "Forbidden" inline).
      return { success: false, error: errorMsg, notEnabled: httpStatus === 403 };
    }
  },

  // POST receptionist-kb/ingest-scrape — body { siteId, employeeId, url }.
  // Returns { success, candidates, chunks, pagesFetched, pagesSkipped } on success.
  async ingestKbScrape({ rootState }, { siteId, url } = {}) {
    if (!siteId) return { success: false, error: 'Site ID is required.' };
    if (!url) return { success: false, error: 'A website address is required.' };
    const employeeId = rootState.customer && rootState.customer.user ? rootState.customer.user.EmployeeID : null;
    const body = buildKbBody({ siteId, employeeId, url });

    try {
      const response = await axios.post('/receptionist-kb/ingest-scrape', body, await botAuth(rootState));
      const data = response.data || {};
      return {
        success: true,
        candidates: data.candidates || 0,
        chunks: data.chunks || 0,
        pagesFetched: data.pagesFetched || 0,
        pagesSkipped: data.pagesSkipped || 0,
      };
    } catch (error) {
      console.error('Error importing receptionist KB website:', error.response || error);
      const httpStatus = error.response ? error.response.status : null;
      const errorMsg = error.response?.data?.message || error.message || 'Could not import that website.';
      // 403 -> flag so the component re-lists into the not-enabled state.
      return { success: false, error: errorMsg, notEnabled: httpStatus === 403 };
    }
  }
};

const getters = {
  isLoading: state => state.isLoading,
  error: state => state.error,
  allBotStats: state => state.stats,
  elevenLabsConversations: state => state.elevenLabsConversations,
  elevenLabsConversationDetail: state => state.elevenLabsConversationDetail,
  elevenLabsConversationPagination: state => state.elevenLabsConversationPagination,
  isLoadingElevenLabsConversations: state => state.isLoadingElevenLabsConversations,
  isLoadingElevenLabsConversationDetail: state => state.isLoadingElevenLabsConversationDetail,
  elevenLabsConversationError: state => state.elevenLabsConversationError,
  siteCalls: state => state.siteCalls,
  siteCallsNextCursor: state => state.siteCallsNextCursor,
  isLoadingSiteCalls: state => state.isLoadingSiteCalls,
  siteCallsError: state => state.siteCallsError,
  kbItems: state => state.kbItems,
  isLoadingKb: state => state.isLoadingKb,
  kbError: state => state.kbError,
  kbAccessState: state => state.kbAccessState,
  kbUnknownDefault: state => state.kbUnknownDefault,
  dashboardStats: state => state.dashboard,
  dashboardLoading: state => state.dashboardLoading,
  dashboardAccess: state => state.dashboardAccess,
  dashboardRange: state => state.dashboardRange,
};

export default {
  namespaced,
  state,
  mutations,
  actions,
  getters
}; 